// Utility functions for talking to the Excel workbook from the task pane.
export async function getActiveSelection() {
    let result = null;
    try {
        await Excel.run(async (context) => {
            const range = context.workbook.getSelectedRange();
            range.load("address,formulas,values");
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            sheet.load("name");
            await context.sync();
            const address = range.address;
            const formula = range.formulas[0][0];
            const value = range.values[0][0];
            const sheetName = sheet.name;
            const cellAddress = address.includes("!") ? address.split("!")[1] : address;
            // formulas/values above are the top-left cell's, so expose that cell
            // separately from the full selection address.
            const cell = cellAddress.split(":")[0];
            result = { address: cellAddress, cell, sheet: sheetName, formula, value };
        });
    }
    catch {
        // Selection might span multiple areas or be invalid – ignore.
    }
    return result;
}
// Full-column (A:A) and full-row (1:3) refs span the whole sheet; reading them
// raw would load ~1M rows. They get clipped to the sheet's used range instead.
const FULL_SPAN_RE = /^([A-Z]{1,3}:[A-Z]{1,3}|\d{1,7}:\d{1,7})$/;
export async function readPrecedentValuesAndFormulas(precedents) {
    const values = new Array(precedents.length).fill(null);
    const formulas = new Array(precedents.length).fill(null);
    if (precedents.length === 0)
        return { values, formulas };
    try {
        await Excel.run(async (context) => {
            // Phase 1: resolve sheets without throwing, so one missing sheet (deleted,
            // renamed, or a mis-parsed reference) doesn't blank out the whole batch.
            const sheets = precedents.map((prec) => {
                const ws = context.workbook.worksheets.getItemOrNullObject(prec.sheet);
                ws.load("isNullObject");
                return ws;
            });
            await context.sync();
            // Phase 2: for full-column/row refs, fetch the used range to clip against.
            const usedRanges = new Map();
            precedents.forEach((prec, i) => {
                if (sheets[i].isNullObject || !FULL_SPAN_RE.test(prec.ref))
                    return;
                const used = sheets[i].getUsedRangeOrNullObject(true);
                used.load("isNullObject");
                usedRanges.set(i, used);
            });
            if (usedRanges.size > 0)
                await context.sync();
            // Phase 3: load values (clipped where needed).
            const entries = [];
            precedents.forEach((prec, i) => {
                if (sheets[i].isNullObject)
                    return;
                const used = usedRanges.get(i);
                if (used) {
                    if (used.isNullObject) {
                        // Empty sheet: a full-span ref legitimately sums to 0.
                        values[i] = { displayValue: "0", numericValue: 0 };
                        return;
                    }
                    const range = sheets[i].getRange(prec.ref).getIntersectionOrNullObject(used);
                    range.load("isNullObject,values,text");
                    entries.push({ i, range, clipped: true });
                }
                else {
                    const range = sheets[i].getRange(prec.ref);
                    range.load(prec.isRange ? "values,text" : "values,text,formulas");
                    entries.push({ i, range, clipped: false });
                }
            });
            await context.sync();
            for (const { i, range, clipped } of entries) {
                if (clipped && range.isNullObject) {
                    values[i] = { displayValue: "0", numericValue: 0 };
                    continue;
                }
                const prec = precedents[i];
                const vals = range.values;
                const texts = range.text;
                if (prec.isRange) {
                    let sum = 0;
                    for (const row of vals) {
                        for (const cell of row) {
                            if (typeof cell === "number")
                                sum += cell;
                        }
                    }
                    values[i] = {
                        displayValue: sum.toLocaleString(undefined, { maximumFractionDigits: 4 }),
                        numericValue: sum,
                    };
                }
                else {
                    values[i] = {
                        displayValue: texts[0][0],
                        numericValue: typeof vals[0][0] === "number" ? vals[0][0] : null,
                    };
                    const f = range.formulas[0][0];
                    if (typeof f === "string" && f.startsWith("="))
                        formulas[i] = f;
                }
            }
        });
    }
    catch {
        // If anything fails, return whatever was collected before the error.
    }
    return { values, formulas };
}
export async function readDirectDependents(ref, sheetName) {
    if (!Office.context.requirements.isSetSupported("ExcelApi", "1.14")) {
        return { status: "unsupported", items: [] };
    }
    try {
        const items = [];
        await Excel.run(async (context) => {
            const ws = context.workbook.worksheets.getItem(sheetName);
            const range = ws.getRange(ref);
            const deps = range.getDirectDependents();
            deps.load("isNullObject");
            await context.sync();
            if (deps.isNullObject)
                return;
            deps.areas.load("items");
            await context.sync();
            for (const wsAreas of deps.areas.items) {
                wsAreas.load("worksheet/name");
                wsAreas.areas.load("items/address");
            }
            await context.sync();
            for (const wsAreas of deps.areas.items) {
                const sheet = wsAreas.worksheet.name;
                for (const r of wsAreas.areas.items) {
                    const raw = r.address.includes("!")
                        ? r.address.split("!").pop()
                        : r.address;
                    const clean = raw.replace(/\$/g, "");
                    const isRange = clean.includes(":");
                    const display = sheet === sheetName ? clean : `${sheet}!${clean}`;
                    items.push({ ref: clean, sheet, display, isRange, occurrence: 0 });
                }
            }
        });
        return { status: items.length === 0 ? "empty" : "ok", items };
    }
    catch (err) {
        const code = err?.code
            || err?.message
            || "";
        if (/ItemNotFound|no dependent/i.test(code)) {
            return { status: "empty", items: [] };
        }
        if (process.env.NODE_ENV !== "production")
            console.warn("readDirectDependents error:", err);
        return { status: "error", items: [] };
    }
}
export async function checkCellsHaveDependents(cells) {
    const results = new Array(cells.length).fill(false);
    if (!Office.context.requirements.isSetSupported("ExcelApi", "1.14")) {
        return results;
    }
    // getDirectDependents throws for a cell with no dependents, and one throw
    // fails an entire Excel.run batch — so each cell gets its own run.
    await Promise.all(cells.map(async (cell, i) => {
        try {
            await Excel.run(async (context) => {
                const ws = context.workbook.worksheets.getItem(cell.sheet);
                const deps = ws.getRange(cell.ref).getDirectDependents();
                deps.load("isNullObject");
                await context.sync();
                results[i] = !deps.isNullObject;
            });
        }
        catch {
            // No dependents (the API throws for these) or lookup failure — no arrow.
        }
    }));
    return results;
}
export async function navigateToCell(ref, sheetName) {
    try {
        await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getItem(sheetName);
            sheet.activate();
            const range = sheet.getRange(ref);
            range.select();
            await context.sync();
        });
    }
    catch {
        // Sheet may have been deleted/renamed – fail silently.
    }
}
